// Utilitaires pour la gestion des adresses dans les commandes

/**
 * Construit le payload de commande avec la nouvelle logique d'adresses
 * @param {Object} formValues - Valeurs du formulaire
 * @param {Object} context - Contexte contenant user, customer, therapist, etc.
 * @param {string} shippingType - Type d'envoi ('customer' ou 'therapist')
 * @param {boolean} useLegalRepresentative - Utilisation du représentant légal
 * @returns {Object} Payload de commande structuré
 */
export const buildOrderPayload = (formValues, context, shippingType, useLegalRepresentative) => {
    const { user, customer, therapist, capsules } = context;
    const isCustomerShipping = shippingType === 'customer';
    
    return {
        userId: user.id,
        customerId: customer.id,
        capsuleIds: capsules.map(capsule => capsule.id),
        isFree: true,
        shippingType: isCustomerShipping ? 'customer' : 'standard',
        
        // Propriétaires des adresses selon le type d'envoi
        shippingOwnerId: isCustomerShipping ? customer.id : user.id,  // Customer si envoi client, thérapeute si envoi thérapeute
        billingOwnerId: user.id,  // Les adresses de facturation appartiennent TOUJOURS au thérapeute
        
        // IDs d'adresses selon le contexte
        shippingAddressId: formValues.shippingAddressId || null,
        billingAddressId: therapist.addresses?.[0]?.id || null,
        
        addresses: {
            shipping: buildShippingAddress(formValues, context, isCustomerShipping, useLegalRepresentative),
            billing: buildBillingAddress(therapist, formValues)
        },
        
        // Représentant légal structuré
        legalRepresentative: useLegalRepresentative ? {
            firstName: formValues.shipping.legal_first_name || '',
            lastName: formValues.shipping.legal_last_name || ''
        } : null
    };
};

/**
 * Construit l'adresse de livraison
 * @param {Object} values - Valeurs du formulaire
 * @param {Object} context - Contexte
 * @param {boolean} isCustomerShipping - Envoi au customer
 * @param {boolean} useLegalRepresentative - Utilisation du représentant légal
 * @returns {Object} Adresse de livraison formatée
 */
export const buildShippingAddress = (values, context, isCustomerShipping, useLegalRepresentative) => {
    const { customer, therapist } = context;
    const currentUser = isCustomerShipping ? customer : therapist;
    
    const baseAddress = {
        address_1: values.shipping.address_1,
        city: values.shipping.city,
        postcode: values.shipping.postcode,
        state: values.shipping.state === null ? "" : (values.shipping.state || ""),
        country: values.shipping.country,
        phone: values.billing?.phone || "",
        first_name: currentUser?.firstname || "",
        last_name: currentUser?.lastname || "",
    };
    
    // Ajouter les champs du représentant légal UNIQUEMENT si nécessaire
    if (useLegalRepresentative) {
        baseAddress.legal_first_name = values.shipping.legal_first_name || '';
        baseAddress.legal_last_name = values.shipping.legal_last_name || '';
    }
    
    return baseAddress;
};

/**
 * Construit l'adresse de facturation (toujours celle du thérapeute)
 * @param {Object} therapist - Données du thérapeute
 * @param {Object} formValues - Valeurs du formulaire pour le téléphone
 * @returns {Object} Adresse de facturation formatée
 */
export const buildBillingAddress = (therapist, formValues) => {
    const therapistAddress = therapist.addresses?.[0];
    
    if (!therapistAddress) {
        return {
            first_name: therapist.firstname || "",
            last_name: therapist.lastname || "",
            address_1: "",
            address_2: "",
            city: "",
            state: "",
            postcode: "",
            country: "",
            phone: formValues.billing?.phone || therapist.phone || "",
            email: therapist.email || ""
        };
    }
    
    return {
        first_name: therapist.firstname || "",
        last_name: therapist.lastname || "",
        address_1: therapistAddress.street || "",
        address_2: therapistAddress.streetComplement || "",
        city: therapistAddress.city || "",
        state: therapistAddress.state === null ? "" : (therapistAddress.state || ""),
        postcode: therapistAddress.postcode || "",
        country: therapistAddress.isoCountry || "",
        phone: formValues.billing?.phone || therapist.phone || "",
        email: therapist.email || ""
    };
};

/**
 * Valide la cohérence des IDs d'adresses avec le type d'envoi
 * @param {Object} values - Valeurs du formulaire
 * @param {string} shippingType - Type d'envoi
 * @param {Object} customer - Données du customer
 * @param {Object} therapist - Données du thérapeute
 * @returns {Object} Erreurs de validation
 */
export const validateAddressConsistency = (values, shippingType, customer, therapist) => {
    const errors = {};
    
    // Validation pour envoi au customer
    if (shippingType === 'customer') {
        // Si un shippingAddressId est fourni, il doit appartenir au customer
        if (values.shippingAddressId && customer.addresses) {
            const customerAddressExists = customer.addresses.some(
                addr => addr.id === values.shippingAddressId
            );
            if (!customerAddressExists) {
                errors.shippingAddressId = 'Cette adresse n\'appartient pas au client';
            }
        }
    } else {
        // Pour envoi au thérapeute, l'adresse doit appartenir au thérapeute
        if (values.shippingAddressId && therapist.addresses) {
            const therapistAddressExists = therapist.addresses.some(
                addr => addr.id === values.shippingAddressId
            );
            if (!therapistAddressExists) {
                errors.shippingAddressId = 'Cette adresse n\'appartient pas au thérapeute';
            }
        }
    }
    
    return errors;
};

/**
 * Détermine si une nouvelle adresse doit être créée ou si une existante doit être mise à jour
 * @param {number|null} addressId - ID de l'adresse
 * @param {Object} addressData - Données de l'adresse
 * @param {Array} existingAddresses - Adresses existantes
 * @returns {Object} Action à effectuer
 */
export const determineAddressAction = (addressId, addressData, existingAddresses) => {
    if (!addressId) {
        return { action: 'create', data: addressData };
    }
    
    const existingAddress = existingAddresses.find(addr => addr.id === addressId);
    if (!existingAddress) {
        return { action: 'create', data: addressData };
    }
    
    // Comparer les données pour voir si une mise à jour est nécessaire
    const hasChanges = Object.keys(addressData).some(key => {
        const currentValue = addressData[key];
        const existingValue = existingAddress[key];
        return currentValue !== existingValue;
    });
    
    if (hasChanges) {
        return { action: 'update', id: addressId, data: addressData };
    }
    
    return { action: 'none', id: addressId };
};
